import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowsLeftRight, PaperPlaneTilt, CheckCircle, Copy } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/DashboardLayout";
import { PageHeader, Spinner } from "../components/ui-bits";
import { api, getMerchantId, formatDate } from "../lib/api";

export default function Transfers() {
  const merchantId = getMerchantId();
  const [certId, setCertId] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!certId.trim() || !email.trim()) return toast.error("Fill in all fields");
    setLoading(true);
    try {
      const { data } = await api.post("/transfer/initiate", {
        certificate_id: certId.trim(),
        from_id: merchantId,
        from_type: "merchant",
        to_email: email.trim(),
      });
      setResult(data);
      toast.success("Transfer email sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer failed");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setResult(null); setCertId(""); setEmail(""); };

  return (
    <DashboardLayout>
      <PageHeader overline="Ownership" title="Transfers" subtitle="Securely hand over a passport to a new owner. They'll receive an email to accept ownership." />

      <div className="max-w-lg">
        {!result ? (
          <form onSubmit={submit} className="bg-white rounded-2xl border border-navy/10 shadow-luxe p-8 animate-fade-up" data-testid="transfer-form">
            <div className="flex items-center gap-2 text-navy mb-6">
              <div className="h-10 w-10 rounded-xl bg-navy/5 flex items-center justify-center"><ArrowsLeftRight size={20} weight="light" /></div>
              <span className="text-sm uppercase tracking-[0.15em] text-navy/60">Initiate Transfer</span>
            </div>

            <label className="text-[0.62rem] uppercase tracking-[0.2em] text-navy/50">Certificate ID</label>
            <input data-testid="transfer-cert-input" value={certId} onChange={(e) => setCertId(e.target.value)} placeholder="VCN-…" className="mt-2 mb-5 w-full bg-cream border border-navy/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-gold transition-colors text-navy font-mono" />

            <label className="text-[0.62rem] uppercase tracking-[0.2em] text-navy/50">Recipient Email</label>
            <input data-testid="transfer-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="buyer@example.com" className="mt-2 mb-6 w-full bg-cream border border-navy/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-gold transition-colors text-navy" />

            <button data-testid="transfer-submit-button" disabled={loading} className="w-full flex items-center justify-center gap-2 bg-navy text-cream rounded-xl py-4 text-sm hover:brightness-110 active:scale-[0.99] transition-all disabled:opacity-60">
              {loading ? <><Spinner className="h-4 w-4" /> Sending…</> : <><PaperPlaneTilt size={17} weight="light" /> Send Transfer</>}
            </button>
          </form>
        ) : (
          <div className="bg-white rounded-2xl border border-navy/10 shadow-luxe p-8 text-center animate-fade-up" data-testid="transfer-success">
            <CheckCircle size={52} weight="fill" className="mx-auto text-score-green" />
            <h2 className="text-2xl text-navy mt-4">Transfer Sent</h2>
            <p className="text-navy/50 text-sm mt-1">The new owner has been emailed a secure link.</p>
            <p className="text-navy/50 text-sm">Expires {formatDate(result.expiresAt)}.</p>

            <div className="mt-6 bg-cream rounded-xl p-4 flex items-center gap-2">
              <input readOnly value={result.transferUrl} className="flex-1 bg-transparent text-xs text-navy/60 outline-none truncate" />
              <button onClick={() => { navigator.clipboard.writeText(result.transferUrl); toast.success("Copied"); }} className="text-navy hover:text-gold transition-colors"><Copy size={16} /></button>
            </div>

            <button onClick={reset} className="mt-6 text-sm text-navy underline hover:text-gold transition-colors">Start another transfer</button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
